import type {
  ContentReference,
  SearchDocument,
} from '@cfa/pathway-fe-shared/xp';

/**
 * Client-side search dedupe for PATHWAY-3371 until pathway-xp-api collapses duplicate
 * resource ids on GET /search.
 *
 * @todo pathway-xp-api: Dedupe search results by resource id server-side; remove this
 *   module and callers once XP ships (PATHWAY-4127).
 */
export const consolidateSearchDocuments = (
  results: SearchDocument[],
): SearchDocument[] => {
  const seen = new Set<string>();
  const consolidated: SearchDocument[] = [];

  for (const document of results) {
    const id = document.id;
    if (!id) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    consolidated.push(document);
  }

  return consolidated;
};

export const countSearchRowsForResourceId = (
  results: SearchDocument[],
  resourceId: string,
): number =>
  results.reduce(
    (count, document) => count + (document.id === resourceId ? 1 : 0),
    0,
  );

export const searchResponseHadDuplicateIds = (
  results: SearchDocument[],
): boolean => {
  const seen = new Set<string>();
  for (const document of results) {
    const id = document.id;
    if (!id) continue;
    if (seen.has(id)) return true;
    seen.add(id);
  }
  return false;
};

/**
 * Choose Version references built from the search rows for one resource id,
 * one per content URL (most recently modified row wins).
 */
export const searchRowsToChooserReferences = (
  results: SearchDocument[],
  resourceId: string,
  country: ContentReference['country'],
): ContentReference[] => {
  const byContentUrl = new Map<string, SearchDocument>();
  for (const document of results) {
    if (document.id !== resourceId) continue;
    const key = document.contentUrl ?? document.contentApiUrl;
    if (!key) continue;
    const existing = byContentUrl.get(key);
    if (
      !existing ||
      (document.lastModified ?? '') > (existing.lastModified ?? '')
    ) {
      byContentUrl.set(key, document);
    }
  }

  return [...byContentUrl.values()].map(document => ({
    contentApiUrl: document.contentApiUrl,
    contentUrl: document.contentUrl,
    country,
    lastModified: document.lastModified,
    name: document.name,
    otherContentUrl: document.otherContentUrl,
    otherName: document.otherName,
    otherPrintUrl: document.otherPrintUrl,
    printUrl: document.printUrl,
    sourceSystem: document.sourceSystem,
    tags: document.tags,
  }));
};

/** Resource ids that appeared on more than one search row (pre-dedupe page). */
export const getResourceIdsWithDuplicateSearchRows = (
  results: SearchDocument[],
): string[] => {
  const counts = new Map<string, number>();
  for (const document of results) {
    const id = document.id;
    if (!id) continue;
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  return [...counts.entries()]
    .filter(([, count]) => count > 1)
    .map(([id]) => id);
};
